import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ArrowRight } from 'lucide-react';
import { supabase } from '../../utils/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author_name: string;
  author_avatar: string | null;
  published_at: string;
  category_name: string;
  category_slug: string;
  category_color: string;
  tags: string[];
  avg_read_time_seconds: number;
  featured: boolean;
}

export default function BlogIndex() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  async function fetchPosts() {
    try {
      const { data, error } = await supabase
        .from('blog_posts_published')
        .select('*')
        .order('published_at', { ascending: false });

      if (error) throw error;

      if (data) {
        // Separate featured post
        const featured = data.find(post => post.featured);
        const regular = data.filter(post => !post.featured);

        setFeaturedPost(featured || null);
        setPosts(regular);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }

  // Filter posts based on search and category
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || post.category_slug === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = Array.from(new Set(posts.map(p => ({
    slug: p.category_slug,
    name: p.category_name,
    color: p.category_color
  }))));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (seconds: number) => {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} min read`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-2xl font-display text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b-2 border-dark py-8">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-display mb-4">The DoubleMe Blog</h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              Essays on time, productivity, and multiplying yourself with AI.
            </p>
          </motion.div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-dark rounded-lg font-sans focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full border-2 border-dark font-display transition-all ${
                !selectedCategory
                  ? 'bg-primary text-white'
                  : 'bg-white hover:bg-gray-50'
              }`}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category.slug}
                onClick={() => setSelectedCategory(category.slug)}
                className={`px-4 py-2 rounded-full border-2 border-dark font-display transition-all ${
                  selectedCategory === category.slug
                    ? 'text-white'
                    : 'bg-white hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: selectedCategory === category.slug ? category.color : undefined
                }}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {featuredPost && !selectedCategory && !searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <Link to={`/blog/${featuredPost.slug}`}>
              <div className="bg-gradient-to-br from-primary/5 to-accent/5 border-2 border-dark rounded-2xl overflow-hidden hover:shadow-playful transition-all group">
                <div className="p-8 md:p-12">
                  <div className="mb-4">
                    <span
                      className="inline-block px-4 py-1 rounded-full text-sm font-display border-2 border-dark"
                      style={{ backgroundColor: featuredPost.category_color, color: '#fff' }}
                    >
                      Featured • {featuredPost.category_name}
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-display mb-4 group-hover:text-primary transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      {formatDate(featuredPost.published_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      {formatReadTime(featuredPost.avg_read_time_seconds)}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={`/blog/${post.slug}`}>
                <div className="bg-white border-2 border-dark rounded-xl overflow-hidden hover:shadow-playful hover:-translate-y-1 transition-all group h-full flex flex-col">
                  {/* Category Badge */}
                  <div className="p-4 pb-0">
                    <span
                      className="inline-block px-3 py-1 rounded-full text-xs font-display border-2 border-dark"
                      style={{ backgroundColor: post.category_color, color: '#fff' }}
                    >
                      {post.category_name}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-2xl font-display mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} />
                        {formatReadTime(post.avg_read_time_seconds)}
                      </div>
                    </div>
                  </div>

                  {/* Read More */}
                  <div className="px-6 pb-6">
                    <div className="flex items-center gap-2 text-primary font-display group-hover:gap-4 transition-all">
                      Read More <ArrowRight size={16} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-2xl text-gray-500 font-display">
              No articles found {searchQuery && `for "${searchQuery}"`}
            </p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
              className="mt-4 text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
